/**
 * Whitelabel branding for the pre-login auth pages (login / signup /
 * forgot-password / reset-password).
 *
 * Flow:
 *   1. base.hbs runs an inline head script that sets `window.__IS_WHITELABEL__`
 *      (true on any host that is not a Social Champ domain) and removes the
 *      Social Champ favicon up front so it never flashes on a customer domain.
 *      The full-screen loader is held until branding is applied.
 *   2. This script (bundled, runs as soon as the deferred bundle executes)
 *      fetches the whitelabel config for the current host and applies it, then
 *      reveals the page.
 *
 * The lookup is a SAME-ORIGIN relative request on purpose: a customer domain
 * (e.g. app.brand.com) points at this same server, so `/api/v1/...` resolves to
 * it without CORS. The auth server proxies the request to the publisher, which
 * owns the whitelabel data.
 */

// Social Champ favicon used both for non-branded hosts and as a fallback when a
// whitelabel record has no favicon of its own.
const SC_FAVICON = "/assets/favicon-light.png?cachedd";

function applyFavicon(href) {
  let favicon = document.getElementById("favicon");
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.id = "favicon";
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }
  favicon.href = href;
}

function reveal(mode) {
  window.__WL_READY__ = true;
  document.documentElement.setAttribute("data-wl", mode || "ready");
  if (typeof window.__scHideLoader === "function") {
    window.__scHideLoader();
  }
}

function applyBranding(config) {
  const branding = (config && config.branding) || {};
  const name = branding.name || "";

  // Favicon: whitelabel favicon if set, otherwise keep Social Champ's.
  applyFavicon(branding.favIcon || SC_FAVICON);

  // Title: keep the page intent ("Login", "Sign up", ...) and swap the brand.
  if (name) {
    const intent = (document.title || "").split(" - ")[0] || "";
    document.title = intent ? `${intent} - ${name}` : name;

    const replaceBrandText = (value) =>
      String(value || "").replace(/Social\s+Champ|SocialChamp/g, () => name);
    const setMeta = (selector, value) => {
      const meta = document.querySelector(selector);
      if (meta && value) meta.setAttribute("content", value);
    };
    const tenantUrl = `${window.location.origin}${window.location.pathname}`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", tenantUrl);
    setMeta('meta[name="title"]', document.title);
    setMeta('meta[property="og:title"]', document.title);
    setMeta('meta[name="twitter:title"]', document.title);
    setMeta('meta[property="og:site_name"]', name);
    setMeta('meta[property="og:url"]', tenantUrl);
    [
      'meta[name="description"]',
      'meta[property="og:description"]',
      'meta[name="twitter:description"]',
    ].forEach((selector) => {
      const meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute(
          "content",
          replaceBrandText(meta.getAttribute("content")),
        );
      }
    });
    document
      .querySelectorAll(
        'meta[name="twitter:site"], meta[name="twitter:creator"]',
      )
      .forEach((meta) => meta.remove());

    const socialPreview = branding.loginPageImage || branding.logo;
    if (socialPreview) {
      setMeta('meta[property="og:image"]', socialPreview);
      setMeta('meta[name="twitter:image"]', socialPreview);
    }

    document
      .querySelectorAll("#auth .navbar-brand")
      .forEach((element) => element.setAttribute("aria-label", name));

    // Login errors/modals are rendered by the static Handlebars templates and
    // can appear after the initial page branding has loaded. Rewrite their
    // visible Social Champ token at the same source-of-truth step so inactive
    // account, account-deletion and invitation messages stay tenant branded.
    const replaceBrand = /Social\s+Champ|SocialChamp/g;
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(textNode) {
          const parent = textNode.parentElement;
          if (
            !parent ||
            parent.tagName === "SCRIPT" ||
            parent.tagName === "STYLE" ||
            parent.tagName === "TEXTAREA" ||
            parent.isContentEditable
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      if (replaceBrand.test(node.nodeValue || "")) {
        textNodes.push(node);
      }
      replaceBrand.lastIndex = 0;
    }
    textNodes.forEach((textNode) => {
      textNode.nodeValue = (textNode.nodeValue || "").replace(
        replaceBrand,
        name,
      );
    });
  }

  // Brand logo. `#social-champ-logo` covers login/login1/signup; the broader
  // `#auth .navbar-brand img` catches any other auth-page logo markup (scoped
  // to #auth so the site header logo is left alone).
  const LOGO_SELECTOR = "#social-champ-logo, #auth .navbar-brand img";
  if (branding.logo) {
    document.querySelectorAll(LOGO_SELECTOR).forEach((img) => {
      img.src = branding.logo;
      if (name) img.alt = name;
    });
  }

  // Invite pages (team / client) carry a second, hero "logo" rendered as a CSS
  // background on a `.social-champ-logo` DIV (distinct from the header
  // `#social-champ-logo` IMG), plus a Social Champ icon-bar. Brand the div and
  // hide the icon-bar so the invite hero is tenant-branded too. No-ops on the
  // login page, which has neither element.
  if (branding.logo) {
    document.querySelectorAll("div.social-champ-logo").forEach((el) => {
      el.style.backgroundImage = `url('${branding.logo}')`;
      if (name) el.setAttribute("aria-label", name);
    });
  }
  document.querySelectorAll(".social-champ-icon-bar").forEach((el) => {
    el.style.display = "none";
  });

  // Injected style: constrain the (arbitrary-sized) brand logo and apply the
  // primary colour to buttons / SSO / accents, including hover & active states.
  const css = [];
  if (branding.logo) {
    // Customer logos vary wildly in size — cap them to the header slot.
    css.push(
      `${LOGO_SELECTOR}{max-height:44px!important;width:auto!important;max-width:200px!important;object-fit:contain;}`,
    );
  }
  if (branding.primaryColor) {
    const color = branding.primaryColor;
    css.push(
      `:root{--wl-primary:${color};}`,
      // Solid primary buttons (Login / submit).
      `.btn-primary,.btn-process{background-color:${color}!important;border-color:${color}!important;color:#fff!important;}`,
      `.btn-primary:hover,.btn-primary:focus,.btn-primary:active,.btn-process:hover{background-color:${color}!important;border-color:${color}!important;filter:brightness(0.92);}`,
      // Outline / SSO button: WL-coloured outline, fills with WL colour on hover.
      `.btn-outline-primary,.sso-login-btn{color:${color}!important;border-color:${color}!important;background-color:transparent!important;}`,
      `.btn-outline-primary:hover,.btn-outline-primary:focus,.btn-outline-primary:active,.sso-login-btn:hover,.sso-login-btn:focus{background-color:${color}!important;border-color:${color}!important;color:#fff!important;}`,
      // Links / accents.
      `a.create-link,.create-link,.color-primary,.forgot-password{color:${color}!important;}`,
      // Inline auth loader ("Verifying your credentials" / "Welcome back") — its
      // spinner ring, step check-marks, progress bar and tip highlight are
      // hard-coded to the Social Champ orange in SCSS, so recolor them here.
      `.sc-signup-loader__ring::before{border-top-color:${color}!important;border-right-color:${color}!important;}`,
      `.sc-signup-loader__ring::after{border-bottom-color:${color}!important;border-left-color:${color}!important;}`,
      `.sc-signup-loader__ring-core{background:${color}!important;box-shadow:none!important;}`,
      `.sc-signup-loader__step.is-active .sc-signup-loader__step-icon{border-top-color:${color}!important;}`,
      `.sc-signup-loader__step.is-done .sc-signup-loader__step-icon{border-color:${color}!important;background:${color}!important;}`,
      `.sc-signup-loader__bar-fill{background:${color}!important;}`,
      `.sc-signup-loader__tip b{color:${color}!important;}`,
    );
  }
  if (css.length) {
    const style = document.createElement("style");
    style.setAttribute("data-wl-style", "");
    style.textContent = css.join("");
    document.head.appendChild(style);
  }

  // Right-hand panel: background colour + hero image.
  const right = document.getElementById("right");
  if (right) {
    if (branding.loginPageBgColor) {
      right.style.backgroundColor = branding.loginPageBgColor;
    }
    if (branding.loginPageImage) {
      const heroImg = right.querySelector(".testimonialCard img");
      if (heroImg) {
        heroImg.src = branding.loginPageImage;
        heroImg.alt = name || "";
      } else {
        // Invite pages have no testimonial <img>; their hero image is the panel
        // background itself (an inline style overrides the default banner CSS).
        right.style.backgroundImage = `url('${branding.loginPageImage}')`;
        right.style.backgroundSize = "cover";
        right.style.backgroundPosition = "center";
      }
    }
  }

  // Terms & Privacy links: point them at the customer's own pages when set.
  applyLegalLinks(config);
}

// Terms & Privacy links must NEVER stay pointing at Social Champ's own
// `/terms-of-service/` / `/privacy-policy/` on a white-label host — those
// relative paths don't exist on a tenant domain, so the server falls back to
// the login page and a click just reloads the current page. Point them at the
// tenant's configured URLs when set; otherwise HIDE them.
//
// Called twice by design: once up front for every white-label host (with a null
// config, so the raw links are hidden even when the branding fetch fails or
// returns nothing), and again from applyBranding once the tenant config loads,
// to reveal/rewrite them to the tenant's own URLs.
function applyLegalLinks(config) {
  const set = (selector, url) => {
    document.querySelectorAll(selector).forEach((a) => {
      if (url) {
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.style.display = "";
      } else {
        a.style.display = "none";
      }
    });
  };
  set(
    'a[href="/terms-of-service"], a[href="/terms-of-service/"], a[href="/legal"], a[href="/legal/"]',
    config && config.termsUrl,
  );
  set(
    'a[href="/privacy-policy"], a[href="/privacy-policy/"]',
    config && config.privacyUrl,
  );
}

// Resolve once every given image URL has loaded (or a timeout elapses), so we
// can reveal the page only after the whitelabel imagery is actually painted —
// otherwise the browser keeps showing the old Social Champ image for the
// fraction of a second it takes the new one to download (the "flash").
function preloadImages(urls, timeoutMs) {
  const list = (urls || []).filter(Boolean);
  if (!list.length) return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = list.length;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    list.forEach((url) => {
      const img = new Image();
      img.onload = tick;
      img.onerror = tick;
      img.src = url;
    });
    // Never hold the page hostage on a slow/broken asset.
    setTimeout(finish, timeoutMs || 2500);
  });
}

// White-label domains are login-only: self-service signup is disabled and the
// server bounces /signup -> /login. Remove the on-page signup entry points so a
// user never sees a "Create an account" link that just bounces them back.
// (The Terms/Privacy links share the `.signup-btn` class, so target only the
// account-creation links here.)
function hideSignupEntryPoints() {
  document
    .querySelectorAll(
      '.create-an-account-btn, .donot-have-an-account, a[href="/signup"], a[href^="/signup?"]',
    )
    .forEach(function (el) {
      el.style.display = "none";
    });
}

function loadWhitelabel() {
  // Nothing to do on Social Champ domains — the page is already branded.
  if (!window.__IS_WHITELABEL__) return;

  hideSignupEntryPoints();
  // Neutralize Social Champ's relative legal links immediately, before the
  // async branding fetch resolves — so even if it fails or the host has no
  // config yet, clicking Terms/Privacy never falls back to reloading the login
  // page. applyBranding re-runs this with the tenant's URLs once config loads.
  applyLegalLinks(null);

  // `__WL_DOMAIN__` is set by the early head script (honours the local dev
  // override); fall back to the actual hostname.
  const host = window.__WL_DOMAIN__ || window.location.hostname;
  fetch(`/api/v1/whitelabel/public?domain=${encodeURIComponent(host)}`, {
    credentials: "omit",
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((body) => {
      const config = body && body.data;
      if (config && config.branding) {
        applyBranding(config);
        // Reveal only after the swapped imagery has downloaded, so the old
        // Social Champ logo/hero never flashes while the new ones load.
        return preloadImages(
          [config.branding.logo, config.branding.loginPageImage],
          2500,
        ).then(() => reveal("ready"));
      }
      // Host is not whitelabelled: fall back to Social Champ branding.
      applyFavicon(SC_FAVICON);
      reveal("sc");
    })
    .catch(() => {
      applyFavicon(SC_FAVICON);
      reveal("sc");
    });
}

// Run as soon as the (deferred) bundle executes — the DOM is already parsed.
loadWhitelabel();

export default loadWhitelabel;
